// pages/RankingPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import EventToggle from "../components/EventToggle";
import TopToggle from "../components/TopToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function hexToRgba(hex, alpha = 1) {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  const h = hex.replace("#", "");
  const bigint = parseInt(
    h.length === 3 ? h.split("").map((c) => c + c).join("") : h,
    16
  );
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

// Podest-Farben fuer die Top 3
const MEDALS = {
  1: { ring: "#FFD700", glow: "rgba(255,215,0,0.55)", label: "Gold" },
  2: { ring: "#C7CCD1", glow: "rgba(199,204,209,0.45)", label: "Silber" },
  3: { ring: "#CD7F32", glow: "rgba(205,127,50,0.45)", label: "Bronze" },
};

const fmt = (n) => (n || 0).toLocaleString("de-DE");

export default function RankingPage() {
  const [allTeams, setAllTeams] = useState([]);
  const [maxStations, setMaxStations] = useState(null);
  const [maxStationsForKids, setMaxStationsForKids] = useState(null);
  const [filterEvent, setFilterEvent] = useState("Erwachsene");
  const [topMode, setTopMode] = useState("top10"); // "top10" | "all"
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pulse, setPulse] = useState(0); // triggert den "Live"-Puls bei jedem Refresh

  // Bewegungen: team_id -> { dir: "up" | "down", delta }
  const [movements, setMovements] = useState({});

  const prevRankRef = useRef({}); // team_id -> letzter Rang (Index)
  const clearTimersRef = useRef({});
  const intervalRef = useRef(null);

  // Zuletzt bekannte Stationszahlen. Als Ref, damit ein fehlgeschlagener
  // Abruf die alten Werte behaelt statt auf 0 zu fallen.
  const stationsZahlRef = useRef(null);

  // --- Ranking laden (alle 30 s) ---
  //
  // BEHOBENE FEHLER:
  //  - Die noch moeglichen Punkte wurden mit fest 100 je Station geschaetzt.
  //    Seit das Punkteschema einstellbar ist, stimmt das nicht mehr – das
  //    Backend liefert jetzt die Punkte fuer Platz 1 mit (`max_points`).
  //  - Die Stationszahl wurde nur beim Oeffnen geladen. Der Beamer laeuft den
  //    ganzen Tag; eine spaeter angelegte Station fehlte in der Schaetzung.
  //    Jetzt bei jedem Abruf (die Route ist gecacht und kostet nichts).
  const fetchRanking = async () => {
    try {
      const [rankingRes, zahlRes] = await Promise.all([
        fetch(`${BACKEND_URL}/results-anon`),
        fetch(`${BACKEND_URL}/stations/count`).catch(() => null),
      ]);
      const data = await rankingRes.json();
      if (zahlRes && zahlRes.ok) {
        const z = await zahlRes.json().catch(() => null);
        if (z) {
          stationsZahlRef.current = {
            erwachsene: z.maxStations || 0,
            kinder: z.maxStationsForKids || z.maxStations || 0,
          };
          setMaxStations(stationsZahlRef.current.erwachsene);
          setMaxStationsForKids(stationsZahlRef.current.kinder);
        }
      }

      const zahlen = stationsZahlRef.current;
      const punkteJeStation = Number(data.max_points) > 0 ? Number(data.max_points) : 100;
      const teamsProcessed = (data.teams || []).map((t) => {
        const current = t.total_points || 0;
        const stationsCount = zahlen ? (t.event_type === "Kinder" ? zahlen.kinder : zahlen.erwachsene) : 0;
        const remaining = Math.max(
          (stationsCount - (t.stations_completed || 0)) * punkteJeStation,
          0
        );
        return { ...t, current, remaining };
      });

      setAllTeams(teamsProcessed);
      setLastUpdated(data.last_updated || new Date().toISOString());
      setPulse((p) => p + 1);
    } catch (err) {
      console.error("results-anon error", err);
    }
  };

  // --- Polling alle 30s ---
  useEffect(() => {
    fetchRanking();

    const startPolling = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (document.visibilityState === "visible") fetchRanking();
      }, 30000);
    };

    if (document.visibilityState === "visible") {
      startPolling();
    }

    document.addEventListener("visibilitychange", startPolling);
    return () => {
      document.removeEventListener("visibilitychange", startPolling);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Gefilterte + angezeigte Liste ---
  const filteredTeams = useMemo(
    () =>
      allTeams
        .filter((t) => t.event_type === filterEvent && (t.current || 0) > 0)
        // Platz kommt aus dem Backend (inkl. Gleichstandsregel); nur fuer
        // aeltere Ranking-Dokumente ohne `rank` bleibt die Punkte-Sortierung.
        .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity) || b.current - a.current),
    [allTeams, filterEvent]
  );

  const displayedTeams = useMemo(
    () => (topMode === "top10" ? filteredTeams.slice(0, 10) : filteredTeams),
    [filteredTeams, topMode]
  );

  // Gemeinsame Skala: laengster Gesamtbalken (erreicht + moeglich) auf der Tafel.
  // So ist der laengste ausgefuellte Balken automatisch die Fuehrung – der
  // Balken verstaerkt die Rangfolge, statt ihr zu widersprechen.
  const globalMax = useMemo(
    () =>
      Math.max(
        1,
        ...displayedTeams.map((t) => (t.current || 0) + (t.remaining || 0))
      ),
    [displayedTeams]
  );

  // --- Bei Wechsel von Kategorie/Modus die Bewegungsverfolgung zuruecksetzen,
  //     damit keine falschen Sprunge angezeigt werden. ---
  useEffect(() => {
    prevRankRef.current = {};
    Object.values(clearTimersRef.current).forEach(clearTimeout);
    clearTimersRef.current = {};
    setMovements({});
  }, [filterEvent, topMode]);

  // --- Rang-Aenderungen erkennen und hervorheben ---
  const orderSignature = displayedTeams
    .map((t) => `${t.team_id}:${t.current}`)
    .join("|");

  useEffect(() => {
    const ids = displayedTeams.map((t) => t.team_id);
    const prev = prevRankRef.current;
    const changed = {};

    ids.forEach((id, i) => {
      const before = prev[id];
      if (before !== undefined && before !== i) {
        changed[id] = { dir: i < before ? "up" : "down", delta: Math.abs(before - i) };
      }
    });

    // Neue Rangkarte merken
    const next = {};
    ids.forEach((id, i) => (next[id] = i));
    prevRankRef.current = next;

    if (Object.keys(changed).length) {
      setMovements((m) => ({ ...m, ...changed }));
      Object.keys(changed).forEach((id) => {
        if (clearTimersRef.current[id]) clearTimeout(clearTimersRef.current[id]);
        clearTimersRef.current[id] = setTimeout(() => {
          setMovements((m) => {
            const n = { ...m };
            delete n[id];
            return n;
          });
        }, 4500); // Hervorhebung ~4,5s sichtbar
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSignature]);

  useEffect(() => {
    return () => Object.values(clearTimersRef.current).forEach(clearTimeout);
  }, []);

  const isLive =
    lastUpdated && Date.now() - new Date(lastUpdated).getTime() < 90000;

  return (
    <div className="min-h-full flex flex-col">
      {/* Kopf: Titel + Live-Status */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight text-white leading-none">
            Rangliste
          </h1>
          <div className="mt-2 flex items-center gap-2 text-sm sm:text-base text-gray-400">
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${
                isLive ? "bg-status-success" : "bg-gray-500"
              }`}
              style={
                isLive
                  ? { boxShadow: "0 0 0 0 rgba(34,197,94,0.7)" }
                  : undefined
              }
            >
              {isLive && (
                <motion.span
                  key={pulse}
                  className="block w-2.5 h-2.5 rounded-full bg-status-success"
                  initial={{ scale: 1, opacity: 0.7 }}
                  animate={{ scale: 3, opacity: 0 }}
                  transition={{ duration: 1.6, ease: "easeOut" }}
                />
              )}
            </span>
            <span>
              {isLive ? "Live" : "Zuletzt"} ·{" "}
              {lastUpdated ? new Date(lastUpdated).toLocaleTimeString("de-DE") : "…"}
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <EventToggle value={filterEvent} onChange={setFilterEvent} />
          <TopToggle value={topMode} onChange={setTopMode} />
        </div>
      </div>

      {/* Legende + Stationen */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-xs sm:text-sm text-gray-400">
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 rounded bg-primary-500"></div>
          <span>Erreichte Punkte</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 rounded bg-primary-500/25"></div>
          <span>Noch möglich</span>
        </div>
        <div className="ml-auto text-gray-500">
          Max. Stationen · Erwachsene {maxStations ?? "…"} · Kinder{" "}
          {maxStationsForKids ?? "…"}
        </div>
      </div>

      {/* Board */}
      <div className="flex-1">
        {displayedTeams.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500 text-lg">
            Noch keine Ergebnisse vorhanden.
          </div>
        ) : (
          <div className="flex flex-col gap-2.5 sm:gap-3">
            <AnimatePresence initial={false}>
              {displayedTeams.map((team, index) => {
                const rank = team.rank ?? index + 1;
                const medal = MEDALS[rank];
                const achieved = team.current || 0;
                const remaining = team.remaining || 0;
                const stationsCount =
                  team.event_type === "Kinder" ? maxStationsForKids : maxStations;

                const pctAchieved = (achieved / globalMax) * 100;
                const pctRemaining = (remaining / globalMax) * 100;
                const rgbaRemaining = hexToRgba(team.color || "#4B5563", 0.28);

                const move = movements[team.team_id];
                const glow =
                  move?.dir === "up"
                    ? "0 0 0 2px rgba(34,197,94,0.9), 0 0 26px 4px rgba(34,197,94,0.45)"
                    : move?.dir === "down"
                    ? "0 0 0 2px rgba(248,113,113,0.7), 0 0 22px 2px rgba(248,113,113,0.30)"
                    : "0 0 0 0 rgba(0,0,0,0)";

                return (
                  <motion.div
                    layout
                    key={team.team_id}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      boxShadow: move ? [glow, glow, "0 0 0 0 rgba(0,0,0,0)"] : "0 0 0 0 rgba(0,0,0,0)",
                    }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{
                      layout: { type: "spring", stiffness: 130, damping: 22 },
                      boxShadow: { duration: 4.2, ease: "easeOut", times: [0, 0.35, 1] },
                      opacity: { duration: 0.4 },
                    }}
                    className="relative flex items-center gap-3 sm:gap-5 rounded-xl bg-secondary-900 px-3 sm:px-5 py-3 sm:py-4"
                    style={{
                      borderLeft: `5px solid ${team.color || "#4B5563"}`,
                    }}
                  >
                    {/* Rang / Medaille */}
                    <div className="shrink-0 flex items-center justify-center">
                      <div
                        className={`flex items-center justify-center rounded-full font-black tabular-nums ${
                          medal
                            ? "text-secondary-950"
                            : "text-gray-300 bg-secondary-800"
                        }`}
                        style={{
                          width: "clamp(2.75rem, 4vw, 4rem)",
                          height: "clamp(2.75rem, 4vw, 4rem)",
                          fontSize: "clamp(1.25rem, 2vw, 2rem)",
                          background: medal ? medal.ring : undefined,
                          boxShadow: medal ? `0 0 18px ${medal.glow}` : undefined,
                        }}
                      >
                        {rank}
                      </div>
                    </div>

                    {/* Hauptteil */}
                    <div className="flex-1 min-w-0">
                      {/* Kopfzeile: Name + Punkte + Bewegung */}
                      <div className="flex items-center justify-between gap-3 mb-1.5">
                        {/* Bewusst KEIN Team-Name: Die Rangliste bleibt anonym,
                            damit die Preisverleihung nicht vorweggenommen wird.
                            Ein wandernder Balken bleibt allein an seiner Farbe
                            wiedererkennbar. */}
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span
                            className="shrink-0 rounded"
                            style={{
                              width: "2.25rem",
                              height: "0.9rem",
                              background: team.color || "#4B5563",
                            }}
                          />
                          <span className="shrink-0 text-xs sm:text-sm text-gray-500">
                            {team.stations_completed || 0}/{stationsCount || "?"} Stationen
                          </span>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                          {/* Bewegungs-Badge */}
                          <div className="w-14 sm:w-16 flex justify-end">
                            <AnimatePresence>
                              {move && (
                                <motion.span
                                  initial={{ opacity: 0, scale: 0.6, y: move.dir === "up" ? 6 : -6 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  exit={{ opacity: 0, scale: 0.6 }}
                                  className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-sm sm:text-base font-bold ${
                                    move.dir === "up"
                                      ? "bg-status-success/20 text-status-success"
                                      : "bg-status-error/20 text-red-400"
                                  }`}
                                >
                                  {move.dir === "up" ? "▲" : "▼"}
                                  {move.delta}
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </div>
                          {/* Punkte */}
                          <div className="text-right">
                            <span className="font-black tabular-nums text-white text-2xl sm:text-3xl xl:text-4xl leading-none">
                              {fmt(achieved)}
                            </span>
                            <span className="ml-1 text-xs sm:text-sm text-gray-400">Pkt</span>
                          </div>
                        </div>
                      </div>

                      {/* Fortschrittsbalken: erreicht + noch moeglich */}
                      <div className="w-full bg-secondary-800/70 rounded-md h-3.5 sm:h-4 overflow-hidden flex">
                        <div
                          style={{
                            width: `${pctAchieved}%`,
                            background: team.color || "#4B5563",
                            transition: "width 1000ms ease",
                          }}
                          className="h-full"
                        />
                        <div
                          style={{
                            width: `${pctRemaining}%`,
                            background: rgbaRemaining,
                            transition: "width 1000ms ease",
                          }}
                          className="h-full"
                        />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
